import { blake2b256 } from "../crypto/blake2b.js";
import { decrypt, encrypt } from "../crypto/xchacha20poly1305.js";
import {
  KEY_DERIVATION_HINT,
  PAYLOAD_VERSION,
  PROTOCOL_VERSION,
} from "./constants.js";
import {
  canonicalCrdtBytes,
  CrdtState,
  emptyCrdtState,
} from "../crdt/merge.js";

export interface PlaintextEnvelope {
  protocol_version: number;
  payload_version: number;
  content_hash: string;
  updated_at: string;
  updated_by_device_id: string;
  crdt_state: CrdtState;
}

export interface EncryptedBlob {
  ciphertext_base64: string;
  content_nonce_base64: string;
  key_derivation_hint: string;
  device_id: string;
}

export function computeContentHash(state: CrdtState): string {
  return blake2b256(canonicalCrdtBytes(state));
}

export function buildEnvelope(
  state: CrdtState,
  deviceId: string,
  updatedAt = new Date().toISOString(),
): PlaintextEnvelope {
  return {
    protocol_version: PROTOCOL_VERSION,
    payload_version: PAYLOAD_VERSION,
    content_hash: computeContentHash(state),
    updated_at: updatedAt,
    updated_by_device_id: deviceId,
    crdt_state: state,
  };
}

export function verifyContentHash(envelope: PlaintextEnvelope): void {
  const expected = computeContentHash(envelope.crdt_state);
  if (expected !== envelope.content_hash) {
    throw new Error("content_hash_mismatch");
  }
}

export function encodeEnvelope(envelope: PlaintextEnvelope): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(envelope));
}

export function decodeEnvelope(bytes: Uint8Array): PlaintextEnvelope {
  const envelope = JSON.parse(new TextDecoder().decode(bytes)) as PlaintextEnvelope;
  if (envelope.protocol_version !== PROTOCOL_VERSION) {
    throw new Error("unsupported_protocol_version");
  }
  verifyContentHash(envelope);
  return envelope;
}

export function encryptEnvelope(
  syncKey: Uint8Array,
  state: CrdtState,
  deviceId: string,
  nonce?: Uint8Array,
  updatedAt?: string,
): EncryptedBlob {
  const envelope = buildEnvelope(state, deviceId, updatedAt);
  const plaintext = encodeEnvelope(envelope);
  const { ciphertext, nonce: usedNonce } = encrypt(syncKey, plaintext, nonce);

  return {
    ciphertext_base64: Buffer.from(ciphertext).toString("base64"),
    content_nonce_base64: Buffer.from(usedNonce).toString("base64"),
    key_derivation_hint: KEY_DERIVATION_HINT,
    device_id: deviceId,
  };
}

export function decryptEnvelope(syncKey: Uint8Array, blob: EncryptedBlob): PlaintextEnvelope {
  if (blob.key_derivation_hint && blob.key_derivation_hint !== KEY_DERIVATION_HINT) {
    throw new Error("unsupported_key_derivation_hint");
  }
  const ciphertext = Buffer.from(blob.ciphertext_base64, "base64");
  const nonce = Buffer.from(blob.content_nonce_base64, "base64");
  const plaintext = decrypt(syncKey, ciphertext, nonce);
  const envelope = decodeEnvelope(plaintext);
  return envelope;
}

export function emptyEncryptedBootstrap(
  syncKey: Uint8Array,
  deviceId: string,
): EncryptedBlob {
  return encryptEnvelope(syncKey, emptyCrdtState(), deviceId);
}
